import React, { useEffect, useState, useCallback } from 'react';
import { Box, Stack, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader/PageHeader';
import CustomTabs from '../../components/CustomTabs/CustomTabs';
import { fetchCurrentCycle } from '../HomePage/service';
import { useDispatch, useSelector } from 'react-redux';
import { getStepStatus } from '../FeedbackCollation/servies';
import { useOkta } from '../../hooks/oktaHook';
import { setStepperStatus } from '../../redux/FeedbackCollation/feedbackCollationSlice';
import { getUrlWithParams, isInvalidValue } from '../../utils/Utils';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { getPbiUrlConfigs, getReportId, getDatasetId, getDisabledTabs } from './PBIReports';
import { fetchPBIEmbedToken } from './services';
import './iframe.css';

function PortfolioDetailView() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useLocation();

  const newParams = new URLSearchParams(search);
  const selectedCycleId = Number(newParams.get('cycleId'));
  const selectedCycle = newParams.get('cycleName');
  const selectedBu = newParams.get('bu');

  const feedbackStepperData = useSelector((state) => state.feedbackCollation.stepperStatus);
  const { userDetails } = useOkta();
  const { roles, gpid } = userDetails;
  const [isInProgress, setIsInProgress] = useState(false);
  const [pbiEmbedToken, setPbiEmbedToken] = useState('');

  const [cardHeaderDetails, setCardHeaderDetails] = useState({
    cycleName: null,
    startTimePeriod: null,
    endTimePeriod: null
  });
  const [disabledTabs, setDisabledTabs] = useState([]);

  const fetchStepperData = useCallback(async () => {
    try {
      const content = await getStepStatus(gpid, roles[0], selectedBu, selectedCycleId);
      dispatch(setStepperStatus(content));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  useEffect(() => {
    getCycleData();
    getPBIEmbedToken(getReportId(), getDatasetId());
    if (!isInvalidValue(gpid) && !isInvalidValue(selectedBu) && !isInvalidValue(roles)) {
      fetchStepperData();
    }
    checkDisabledTabs();
  }, []);

  const checkDisabledTabs = () => {
    const temp = getDisabledTabs();
    setDisabledTabs(temp);
  };
  const getPBIEmbedToken = async (reportId, datasetId) => {
    const embedToken = await fetchPBIEmbedToken(reportId, datasetId);
    setPbiEmbedToken(embedToken);
  };

  useEffect(() => {
    if (feedbackStepperData && feedbackStepperData?.stepperStatus?.length > 0) {
      const stat = feedbackStepperData?.stepperStatus?.some(
        (step) => step?.status === 'In Progress'
      );
      setIsInProgress(stat);
    }
  }, [feedbackStepperData]);

  const tabs = getPbiUrlConfigs();
  const reportId = getReportId();
  const firstTab = tabs.filter((data) => data.id == state?.tabName);
  // State to track the selected tab
  const [selectedTab, setSelectedTab] = useState(state?.tabName || tabs[0].id);
  const [selectedUrl, setSelectedUrl] = useState(firstTab[0]?.pbi_url || tabs[0].pbi_url);

  // track the selected tab and url when clicked from the sidebar
  useEffect(() => {
    if (firstTab && state?.tabName) {
      setSelectedUrl(firstTab[0]?.pbi_url);
      setSelectedTab(state?.tabName);
    }
  }, [state?.tabName]);

  // Handler for tab change
  const handleTabChange = (tabId) => {
    const selectedObj = tabs.filter((data) => data.id == tabId);
    setSelectedTab(tabId);
    setSelectedUrl(selectedObj[0].pbi_url);
  };

  const getCycleData = async () => {
    try {
      const content = await fetchCurrentCycle(selectedBu);
      const data = content.find((item) => item.activeCycleFlag === 'Y');
      setCardHeaderDetails((prevDetails) => ({
        ...prevDetails,
        cycleName: data?.cycleName,
        startTimePeriod: `${data?.startYear}/${data?.startPeriod}`,
        endTimePeriod: `${data?.endYear}/${data?.endPeriod}`
      }));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleFeedbackNavigation = () => {
    if (isInProgress) {
      navigate(
        getUrlWithParams(
          '/alignment-action/feedback-collation',
          selectedBu,
          selectedCycle,
          selectedCycleId
        )
      );
    }
  };

  const randomInt = (minLength, maxLength) => {
    const min = Math.pow(10, minLength - 1);
    const max = Math.pow(10, maxLength) - 1;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const getFilteredURL = (url) => {
    const trimmedBu = encodeURIComponent(selectedBu?.trim());
    const trimmedCycle = encodeURIComponent(selectedCycle?.trim()).replace(/'/g, "%27%27");
    return (
      `${url}&disableSensitivityBanner=true&filter=DimCycle/BusinessUnitName eq '${trimmedBu}' and ` +
      `UserSecurity/BU_NM eq '${trimmedBu}' and DimCycle/CycleName eq '${trimmedCycle}'`
    );
  };

  const getEmbedConfig = (url) => {
    const embedConfig = {
      type: 'report',
      id: reportId,
      embedUrl: getFilteredURL(url),
      accessToken: pbiEmbedToken,
      tokenType: models.TokenType.Embed,
      settings: {
        panes: {
          filters: { visible: false },
          pageNavigation: { visible: false },
          disableSensitivityLabels: true
        },
        layoutType: models.LayoutType.Custom,
        customLayout: {
          displayOption: models.DisplayOption.FitToWidth
        }
      }
    };
    return embedConfig;
  };

  return (
    <Stack gap="16px" sx={{ padding: '24px', width: '100%' }}>
      <PageHeader
        pageHeader="Cycle Analytics"
        cardHeaderDetails={cardHeaderDetails}
        showBu={true}
      />
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column'
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '-0.5rem',
            marginRight: '1rem'
          }}>
          {/* Pass the tabs and selectedTab as props */}
          <CustomTabs
            tabs={tabs}
            key={randomInt(3, 4)}
            selectedTab={selectedTab}
            onTabChange={handleTabChange}
            disabledTabs={disabledTabs}
          />
        </Box>
      </Box>

      <div className="iframe-paper-container">
        {selectedUrl && (
          <div className="iframe-container">
            <div className="iframe-content">
              <PowerBIEmbed
                key={randomInt(3, 4)}
                embedConfig={getEmbedConfig(selectedUrl)}
                cssClassName={'report-style-class'}
              />
            </div>
          </div>
        )}
      </div>
      <Button
        variant="contained"
        color="primary"
        size="small"
        sx={{
          position: 'fixed',
          bottom: '1rem',
          right: '1rem',
          zIndex: 1000 // Makes sure the button is always on top
        }}
        disabled={!isInProgress}
        onClick={handleFeedbackNavigation}>
        Give Feedback
      </Button>
    </Stack>
  );
}

export default PortfolioDetailView;
